import axiosInstance from "../axiosConfig";
import type {
  EditorContent,
  NewsArticleDetail,
  NewsArticleInput,
  NewsArticleStatus,
  NewsMediaAsset,
  NewsStatus,
  NewsVideoAsset,
  PaginatedNews,
} from "./newsService.types";

export const fetchNews = async (params?: { search?: string; page?: number }): Promise<PaginatedNews> => {
  const { data } = await axiosInstance.get("/news/", { params });
  return data;
};

export const fetchNewsStatus = async (): Promise<NewsStatus> => {
  const { data } = await axiosInstance.get("/news/status/");
  return data;
};

export const fetchNewsArticle = async (articleId: number): Promise<NewsArticleDetail> => {
  const { data } = await axiosInstance.get("/news/" + articleId + "/");
  return data;
};

export const fetchManagedNews = async (params?: {
  status?: NewsArticleStatus;
  search?: string;
  titles?: string[];
  statuses?: NewsArticleStatus[];
  updatedFrom?: string;
  updatedTo?: string;
  page?: number;
  pageSize?: number;
  ordering?: string;
}): Promise<PaginatedNews> => {
  const {
    pageSize,
    titles,
    statuses,
    updatedFrom,
    updatedTo,
    status,
    ...rest
  } = params || {};
  const { data } = await axiosInstance.get("/news/manage/articles/", {
    params: {
      ...rest,
      title: titles,
      status: statuses?.length ? statuses : status,
      updated_from: updatedFrom,
      updated_to: updatedTo,
      page_size: pageSize,
    },
  });
  return data;
};

export const createNewsArticle = async (input: NewsArticleInput): Promise<NewsArticleDetail> => {
  const { data } = await axiosInstance.post("/news/manage/articles/", input);
  return data;
};

export const fetchManagedArticle = async (articleId: number): Promise<NewsArticleDetail> => {
  const { data } = await axiosInstance.get("/news/manage/articles/" + articleId + "/");
  return data;
};

export const updateNewsArticle = async (
  articleId: number,
  input: NewsArticleInput,
): Promise<NewsArticleDetail> => {
  const { data } = await axiosInstance.patch("/news/manage/articles/" + articleId + "/", input);
  return data;
};

export const deleteNewsArticle = async (articleId: number): Promise<void> => {
  await axiosInstance.delete("/news/manage/articles/" + articleId + "/");
};

export const publishNewsArticle = async (articleId: number): Promise<NewsArticleDetail> => {
  const { data } = await axiosInstance.post("/news/manage/articles/" + articleId + "/publish/");
  return data.article;
};

export const unpublishNewsArticle = async (articleId: number): Promise<NewsArticleDetail> => {
  const { data } = await axiosInstance.post("/news/manage/articles/" + articleId + "/unpublish/");
  return data;
};

export const uploadNewsMedia = async (
  articleId: number,
  file: File,
): Promise<NewsMediaAsset> => {
  const body = new FormData();
  body.append("file", file);
  const { data } = await axiosInstance.post("/news/manage/articles/" + articleId + "/media/", body, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const deleteNewsMedia = async (articleId: number, mediaId: number): Promise<void> => {
  await axiosInstance.delete("/news/manage/articles/" + articleId + "/media/" + mediaId + "/");
};

export const uploadNewsVideo = async (
  articleId: number,
  file: File,
): Promise<NewsVideoAsset> => {
  const body = new FormData();
  body.append("file", file);
  body.append("article_id", String(articleId));
  const { data } = await axiosInstance.post("/videos/materials/upload", body, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return {
    id: data.id,
    name: data.filename,
    mime: file.type || "video/mp4",
    status: data.status || "processing",
    progress_percent: 0,
    size: file.size,
    playlist_url: "",
  };
};

export const stripTransientAssetUrls = (content: EditorContent): EditorContent => ({
  ...content,
  blocks: (content.blocks || []).map((block) => {
    if (block.type === "newsMedia") {
      const data = { ...(block.data || {}) };
      delete data.url;
      return { ...block, data };
    }
    if (block.type === "newsVideo") {
      const data = { ...(block.data || {}) };
      delete data.playlistUrl;
      return { ...block, data };
    }
    return block;
  }),
});

export const hydrateArticleContent = (
  article: Pick<NewsArticleDetail, "content_json" | "media_assets" | "video_assets">,
): EditorContent => {
  const media = new Map(article.media_assets.map((asset) => [asset.id, asset]));
  const videos = new Map(article.video_assets.map((asset) => [asset.id, asset]));
  return {
    ...article.content_json,
    blocks: (article.content_json.blocks || []).map((block) => {
      if (block.type === "newsMedia") {
        const asset = media.get(Number(block.data && block.data.mediaId));
        return asset
          ? {
              ...block,
              data: {
                ...block.data,
                name: asset.name,
                mime: asset.mime,
                url: asset.url,
              },
            }
          : block;
      }
      if (block.type === "newsVideo") {
        const asset = videos.get(Number(block.data && block.data.videoId));
        return asset
          ? {
              ...block,
              data: {
                ...block.data,
                name: asset.name,
                status: asset.status,
                progressPercent: asset.progress_percent,
                playlistUrl: asset.playlist_url,
              },
            }
          : block;
      }
      return block;
    }),
  };
};